
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BarChart3, TrendingUp, Users, Clock, Calendar, DollarSign, Star, Download } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from "recharts";

const VenueAnalytics = () => {
  const ticketSalesData = [
    { month: 'Jan', sales: 32000 },
    { month: 'Feb', sales: 45000 },
    { month: 'Mar', sales: 67000 },
    { month: 'Apr', sales: 54000 },
    { month: 'May', sales: 78000 },
    { month: 'Jun', sales: 89000 }
  ];

  const creatorPerformanceData = [
    { name: 'DJ Maphorisa', bookings: 12, revenue: 156000, rating: 4.9 },
    { name: 'MC Thabo', bookings: 8, revenue: 89000, rating: 4.7 },
    { name: 'Lens by Lerato', bookings: 15, revenue: 67000, rating: 4.8 },
    { name: 'DJ Prince Kaybee', bookings: 6, revenue: 125000, rating: 4.6 }
  ];

  const peakHoursData = [
    { hour: '18:00', bookings: 15 },
    { hour: '19:00', bookings: 32 },
    { hour: '20:00', bookings: 45 },
    { hour: '21:00', bookings: 67 },
    { hour: '22:00', bookings: 89 },
    { hour: '23:00', bookings: 78 },
    { hour: '00:00', bookings: 56 }
  ];

  const audienceDemographics = [
    { name: '18-25', value: 35, color: '#3B82F6' },
    { name: '26-35', value: 40, color: '#10B981' },
    { name: '36-45', value: 20, color: '#F59E0B' },
    { name: '45+', value: 5, color: '#EF4444' }
  ];

  return (
    <div className="space-y-6">
      <Card className="bg-white/5 border-white/20 backdrop-blur-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Analytics Dashboard
            </CardTitle>
            <Button variant="outline" className="border-white text-white hover:bg-white hover:text-black">
              <Download className="w-4 h-4 mr-2" />
              Export Report
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Key Metrics */}
          <div className="grid md:grid-cols-4 gap-4">
            <Card className="bg-white/5 border-white/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-500/20 rounded-lg">
                    <DollarSign className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">R567K</div>
                    <div className="text-gray-400 text-sm">Total Revenue</div>
                    <div className="text-green-400 text-xs">+23% vs last month</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white/5 border-white/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500/20 rounded-lg">
                    <Users className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">2.4K</div>
                    <div className="text-gray-400 text-sm">Total Attendees</div>
                    <div className="text-blue-400 text-xs">+15% vs last month</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white/5 border-white/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500/20 rounded-lg">
                    <Calendar className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">24</div>
                    <div className="text-gray-400 text-sm">Events Hosted</div>
                    <div className="text-purple-400 text-xs">+8 vs last month</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white/5 border-white/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-500/20 rounded-lg">
                    <Star className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">4.7</div>
                    <div className="text-gray-400 text-sm">Avg Rating</div>
                    <div className="text-orange-400 text-xs">+0.2 vs last month</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Ticket Sales Chart */}
          <Card className="bg-white/5 border-white/20">
            <CardHeader>
              <CardTitle className="text-white text-lg">Ticket Sales Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={ticketSalesData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="month" stroke="#9CA3AF" />
                    <YAxis stroke="#9CA3AF" />
                    <Bar dataKey="sales" fill="#3B82F6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Creator Performance */}
          <Card className="bg-white/5 border-white/20">
            <CardHeader>
              <CardTitle className="text-white text-lg">Best-Performing Creators</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {creatorPerformanceData.map((creator, index) => (
                  <div key={creator.name} className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <div className="text-white font-bold text-2xl">#{index + 1}</div>
                      </div>
                      <div className="w-10 h-10 bg-gradient-to-br from-white to-gray-400 rounded-full"></div>
                      <div>
                        <div className="text-white font-medium">{creator.name}</div>
                        <div className="text-gray-400 text-sm">
                          {creator.bookings} bookings • R{creator.revenue.toLocaleString()} revenue
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1 mb-1">
                        <Star className="w-4 h-4 text-yellow-400 fill-current" />
                        <span className="text-white font-medium">{creator.rating}</span>
                      </div>
                      <Badge className="bg-green-500/20 text-green-400">Top Performer</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Peak Hours & Demographics */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-white/5 border-white/20">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Peak Booking Hours
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={peakHoursData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="hour" stroke="#9CA3AF" />
                      <YAxis stroke="#9CA3AF" />
                      <Line type="monotone" dataKey="bookings" stroke="#10B981" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border-white/20">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Audience Demographics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={audienceDemographics}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}%`}
                      >
                        {audienceDemographics.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Insights */}
          <Card className="bg-white/5 border-white/20">
            <CardHeader>
              <CardTitle className="text-white text-lg">Key Insights & Recommendations</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-5 h-5 text-green-400" />
                    <span className="text-green-400 font-medium">Growth Opportunity</span>
                  </div>
                  <p className="text-gray-300">
                    Saturday events show 40% higher attendance. Consider adding more weekend slots.
                  </p>
                </div>
                
                <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-5 h-5 text-blue-400" />
                    <span className="text-blue-400 font-medium">Top Performer</span>
                  </div>
                  <p className="text-gray-300">
                    DJ Maphorisa consistently delivers high ratings and revenue. Consider exclusive partnership.
                  </p>
                </div>
                
                <div className="p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-5 h-5 text-orange-400" />
                    <span className="text-orange-400 font-medium">Peak Hours</span>
                  </div>
                  <p className="text-gray-300">
                    Most bookings happen between 9-11 PM. Optimize staffing and service during these hours.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    </div>
  );
};

export default VenueAnalytics;
